'use client';

import { motion } from 'framer-motion';
import { Star, Heart, ArrowRight, Package } from 'lucide-react';
import { useDesign } from '@/context/DesignContext';
import { getImageUrl } from '@/lib/utils';

interface ProductsSectionProps {
  title?: string;
  subtitle?: string;
  viewAllText?: string;
  products?: Array<{
    id: number;
    name: string;
    brand: string;
    price: number;
    originalPrice: number;
    rating: number;
    reviews: number;
    badge?: string;
    description: string;
    image?: string | null;
    stock?: number;
    category?: { id: number; name: string } | null;
  }>;
  design?: any;
}

export function ProductsSection({
  title = 'Nos Coups de Cœur',
  subtitle = 'Une sélection rigoureuse de nos produits les plus appréciés par nos clients',
  viewAllText = 'Voir tous les produits',
  products = [],
  design: propDesign
}: ProductsSectionProps) {
  const contextDesign = useDesign();
  const design = propDesign || contextDesign;

  if (!products || products.length === 0) return null;

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: { opacity: 1, transition: { staggerChildren: 0.15 } }
  };
  const itemVariants = {
    hidden: { y: 30, opacity: 0 },
    visible: { y: 0, opacity: 1, transition: { duration: 0.6, ease: "easeOut" as const } }
  };

  const sectionBg = design?.componentDesignMap?.ProductsSection?.sectionBackground || design?.colors?.backgroundAlt || '#F8FAFC';
  const titleColor = design?.componentDesignMap?.ProductsSection?.titleColor || design?.colors?.text || '#0F172A';
  const subtitleColor = design?.componentDesignMap?.ProductsSection?.subtitleColor || design?.colors?.textLight || '#64748B';
  const cardBg = design?.componentDesignMap?.ProductsSection?.cardBackground || '#FFFFFF';
  const cardBorder = design?.componentDesignMap?.ProductsSection?.cardBorderColor || design?.colors?.border || '#E2E8F0';
  const cardShadow = design?.componentDesignMap?.ProductsSection?.cardShadow || design?.effects?.boxShadow || '0 10px 25px -5px rgba(0,0,0,0.05)';
  const cardHoverShadow = design?.componentDesignMap?.ProductsSection?.cardHoverShadow || design?.effects?.hoverShadow || '0 20px 25px -5px rgba(0,0,0,0.1)';
  const imageBg = design?.componentDesignMap?.ProductsSection?.imageBackground || 'from-slate-100 to-slate-200';
  const badgeBg = design?.componentDesignMap?.ProductsSection?.badgeBackground || design?.colors?.text || '#0F172A';
  const badgeTextColor = design?.componentDesignMap?.ProductsSection?.badgeTextColor || '#FFFFFF';
  const brandColor = design?.componentDesignMap?.ProductsSection?.brandColor || design?.colors?.textLight || '#64748B';
  const productNameColor = design?.componentDesignMap?.ProductsSection?.productNameColor || design?.colors?.text || '#0F172A';
  const productNameHoverColor = design?.componentDesignMap?.ProductsSection?.productNameHoverColor || design?.colors?.primary || '#3B82F6';
  const descColor = design?.componentDesignMap?.ProductsSection?.descriptionColor || design?.colors?.textLight || '#64748B';
  const ratingColor = design?.componentDesignMap?.ProductsSection?.ratingColor || design?.colors?.accent || '#F59E0B';
  const ratingTextColor = design?.componentDesignMap?.ProductsSection?.ratingTextColor || design?.colors?.textLight || '#64748B';
  const priceColor = design?.componentDesignMap?.ProductsSection?.priceColor || design?.colors?.text || '#0F172A';
  const originalPriceColor = design?.componentDesignMap?.ProductsSection?.originalPriceColor || design?.colors?.textLight || '#64748B';
  const addBtnBg = design?.componentDesignMap?.ProductsSection?.addButtonBackground || design?.colors?.text || '#0F172A';
  const addBtnTextColor = design?.componentDesignMap?.ProductsSection?.addButtonTextColor || '#FFFFFF';
  const addBtnHoverBg = design?.componentDesignMap?.ProductsSection?.addButtonHoverBackground || '#1E293B';
  const favBtnBg = design?.componentDesignMap?.ProductsSection?.favoriteButtonBackground || '#FFFFFF';
  const favBtnShadow = design?.componentDesignMap?.ProductsSection?.favoriteButtonShadow || design?.effects?.boxShadow || '0 10px 25px -5px rgba(0,0,0,0.05)';

  return (
    <section className="py-20 lg:py-32" style={{ background: sectionBg }}>
      <div className="container mx-auto px-6">
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="flex flex-col md:flex-row md:items-end justify-between mb-16"
        >
          <div>
            <h2 className="text-4xl lg:text-5xl font-bold mb-4" style={{ color: titleColor }}>{title}</h2>
            <p className="text-lg max-w-xl" style={{ color: subtitleColor }}>{subtitle}</p>
          </div>
          <motion.button
            whileHover={{ scale: 1.02 }}
            className="mt-6 md:mt-0 flex items-center gap-2 font-semibold"
            style={{ color: titleColor }}
          >
            <span>{viewAllText}</span>
            <ArrowRight className="w-5 h-5" style={{ color: titleColor }} />
          </motion.button>
        </motion.div>

        <motion.div 
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8"
        >
          {products.map((product) => (
            <motion.div
              key={product.id}
              variants={itemVariants}
              whileHover={{ y: -8 }}
              className="group overflow-hidden rounded-2xl transition-all border"
              style={{ background: cardBg, borderColor: cardBorder, boxShadow: cardShadow }}
              onMouseEnter={(e) => e.currentTarget.style.boxShadow = cardHoverShadow}
              onMouseLeave={(e) => e.currentTarget.style.boxShadow = cardShadow}
            >
              <div className={`relative aspect-square bg-gradient-to-br ${imageBg} p-6 flex items-center justify-center`}>
                {product.image ? (
                  <img
                    src={getImageUrl(product.image)}
                    alt={product.name}
                    className="w-full h-full object-cover rounded-t-2xl"
                  />
                ) : (
                  <Package className="w-24 h-24 group-hover:scale-110 transition-transform" style={{ color: design?.colors?.textLight || '#94A3B8' }} />
                )}
                {product.badge && (
                  <div className="absolute top-4 left-4">
                    <span className="px-3 py-1 text-xs font-semibold rounded-full" style={{ background: badgeBg, color: badgeTextColor }}>{product.badge}</span>
                  </div>
                )}
                {product.stock !== undefined && (
                  <div className="absolute bottom-4 right-4">
                    <span className={`px-3 py-1 text-xs font-semibold rounded-full ${product.stock > 0 ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                      {product.stock > 0 ? `${product.stock} en stock` : 'Rupture'}
                    </span>
                  </div>
                )}
                <motion.button
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.9 }}
                  className="absolute top-4 right-4 w-10 h-10 rounded-full flex items-center justify-center shadow-md opacity-0 group-hover:opacity-100 transition-opacity"
                  style={{ background: favBtnBg, boxShadow: favBtnShadow }}
                >
                  <Heart className="w-5 h-5" style={{ color: design?.colors?.textLight || '#64748B' }} />
                </motion.button>
              </div>
              <div className="p-6">
                <p className="text-sm mb-1" style={{ color: brandColor }}>{product.brand}</p>
                <h3 className="text-lg font-bold mb-1 transition-colors group-hover:text-blue-600" style={{ color: productNameColor }}>
                  {product.name}
                </h3>
                {product.category && (
                  <p className="text-xs mb-1" style={{ color: brandColor }}>Catégorie: {product.category.name}</p>
                )}
                <p className="text-sm mb-4 line-clamp-2" style={{ color: descColor }}>{product.description}</p>
                {/* <div className="flex items-center gap-1 mb-4">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} className="w-4 h-4" style={{ color: i < Math.floor(product.rating) ? ratingColor : '#D1D5DB', fill: i < Math.floor(product.rating) ? ratingColor : 'none' }} />
                  ))}
                  <span className="text-sm ml-2" style={{ color: ratingTextColor }}>({product.reviews})</span>
                </div> */}
                <div className="flex items-center justify-between">
                  <div>
                    <span className="text-xl  ml-2" style={{ color: originalPriceColor }}>{product.price} TND</span>
                    {/* <span className="text-sm line-through ml-2" style={{ color: originalPriceColor }}>{product.originalPrice} TND</span> */}
                  </div>
                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    className="px-4 py-2 rounded-full text-sm font-medium transition-colors"
                    style={{ background: addBtnBg, color: addBtnTextColor }}
                    onMouseEnter={(e) => e.currentTarget.style.background = addBtnHoverBg}
                    onMouseLeave={(e) => e.currentTarget.style.background = addBtnBg}
                  >
                    Ajouter
                  </motion.button>
                </div>
              </div>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
}